import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- 标签管理器（管理员使用）
// ============================================================================
import * as React from 'react';
import { getPostApiClient } from '../post-api-client.js';
import { useTags } from '../hooks/use-tags.js';
/**
 * 标签管理组件
 */
export function TagManager({ onUpdated }) {
    const { tags, loading, error, refresh } = useTags();
    const [name, setName] = React.useState('');
    const [slug, setSlug] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [deletingSlug, setDeletingSlug] = React.useState(null);
    const [localError, setLocalError] = React.useState(null);
    const [successMsg, setSuccessMsg] = React.useState(null);
    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim())
            return;
        setSubmitting(true);
        setLocalError(null);
        setSuccessMsg(null);
        try {
            await getPostApiClient().createTag({
                name: name.trim(),
                slug: slug.trim() || undefined,
            });
            setName('');
            setSlug('');
            setSuccessMsg('标签创建成功');
            refresh();
            onUpdated?.();
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : '创建标签失败');
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleDelete = async (targetSlug) => {
        if (!confirm(`确定要删除标签 "${targetSlug}" 吗？相关文章的标签将被移除。`))
            return;
        setDeletingSlug(targetSlug);
        setLocalError(null);
        setSuccessMsg(null);
        try {
            await getPostApiClient().deleteTag(targetSlug);
            setSuccessMsg(`标签 "${targetSlug}" 已删除`);
            refresh();
            onUpdated?.();
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : '删除标签失败');
        }
        finally {
            setDeletingSlug(null);
        }
    };
    return (_jsxs("div", { style: styles.container, children: [_jsx("h3", { style: styles.title, children: "\u6807\u7B7E\u7BA1\u7406" }), _jsx("form", { onSubmit: handleCreate, style: styles.form, children: _jsxs("div", { style: styles.formRow, children: [_jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "\u6807\u7B7E\u540D\u79F0", required: true, style: styles.input }), _jsx("input", { type: "text", value: slug, onChange: (e) => setSlug(e.target.value), placeholder: "slug\uFF08\u53EF\u9009\uFF09", style: styles.input }), _jsx("button", { type: "submit", disabled: submitting || !name.trim(), style: styles.createButton, children: submitting ? '创建中...' : '创建' })] }) }), localError && _jsx("div", { style: styles.error, children: localError }), successMsg && _jsx("div", { style: styles.success, children: successMsg }), loading ? (_jsx("div", { style: styles.loading, children: "\u52A0\u8F7D\u4E2D..." })) : error ? (_jsx("div", { style: styles.error, children: error })) : tags.length === 0 ? (_jsx("p", { style: styles.emptyText, children: "\u6682\u65E0\u6807\u7B7E" })) : (_jsx("div", { style: styles.tagCloud, children: tags.map(tag => (_jsxs("div", { style: styles.tagItem, children: [_jsxs("span", { style: styles.tagLabel, children: ["#", tag.name] }), _jsx("span", { style: styles.tagCount, children: tag.postCount }), _jsx("button", { onClick: () => handleDelete(tag.slug), disabled: deletingSlug === tag.slug, style: styles.deleteButton, title: "\u5220\u9664\u6807\u7B7E", children: deletingSlug === tag.slug ? '...' : '×' })] }, tag.slug))) }))] }));
}
const styles = {
    container: {
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    title: {
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: 600,
        color: '#1e293b',
    },
    form: {
        marginBottom: '16px',
    },
    formRow: {
        display: 'flex',
        gap: '8px',
    },
    input: {
        flex: 1,
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '13px',
        outline: 'none',
    },
    createButton: {
        padding: '8px 16px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    tagCloud: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    tagItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        backgroundColor: '#eef2ff',
        borderRadius: '16px',
        border: '1px solid #c7d2fe',
    },
    tagLabel: {
        fontSize: '13px',
        color: '#4338ca',
    },
    tagCount: {
        fontSize: '11px',
        color: '#818cf8',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '1px 6px',
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        color: '#818cf8',
        cursor: 'pointer',
        fontSize: '14px',
        padding: '0 2px',
        lineHeight: 1,
    },
    error: {
        padding: '8px 12px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#dc2626',
        marginBottom: '12px',
    },
    success: {
        padding: '8px 12px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#166534',
        marginBottom: '12px',
    },
    loading: {
        padding: '20px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '13px',
    },
    emptyText: {
        margin: 0,
        padding: '20px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '13px',
    },
};
//# sourceMappingURL=tag-manager.js.map